import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, cn } from "@nexus/ui";

/** Mirrors the bounds `AdminOpsSettingsSchema` puts on every budget. */
const MIN_SECONDS = 30;
const MAX_SECONDS = 24 * 60 * 60;
const MAX_OVERRIDES = 50;

/**
 * Per-event-type end-to-end budgets, for the types where the default is wrong.
 *
 * Generating opportunities and sending an invite are not the same kind of wait,
 * so one number for the whole system either alerts constantly on the slow ones
 * or never on the fast ones. Rows rather than a JSON box because the value is
 * bounded and the bounds are worth enforcing while it is being typed.
 */
export function SlowActionBudgetOverrides({
  value,
  disabled,
  onChange,
}: {
  value: Record<string, number>;
  disabled: boolean;
  onChange: (next: Record<string, number>) => void;
}) {
  const { t } = useTranslation();
  const [newEventType, setNewEventType] = useState("");

  const entries = Object.entries(value).sort(([a], [b]) => a.localeCompare(b));
  const isFull = entries.length >= MAX_OVERRIDES;
  const trimmed = newEventType.trim();
  const canAdd = trimmed.length > 0 && !(trimmed in value) && !isFull && !disabled;

  function setSeconds(eventType: string, seconds: number) {
    onChange({ ...value, [eventType]: clamp(seconds) });
  }

  function remove(eventType: string) {
    const next = { ...value };
    delete next[eventType];
    onChange(next);
  }

  function add() {
    if (!canAdd) return;
    onChange({ ...value, [trimmed]: MIN_SECONDS * 10 });
    setNewEventType("");
  }

  return (
    <div className={cn("flex flex-col gap-2 px-5 py-3", disabled && "opacity-60")}>
      <p className="m-0 text-xs text-muted-foreground">
        {t(
          "admin.opsSettings.slowActionOverridesHelp",
          "Event types that need their own budget. Anything not listed uses the default above.",
        )}
      </p>

      {entries.map(([eventType, seconds]) => (
        <div key={eventType} className="flex items-center gap-2">
          <span className="flex-1 truncate font-mono text-[11px] text-foreground">{eventType}</span>
          <Input
            type="number"
            className="w-24 text-right tabular-nums"
            aria-label={t("admin.opsSettings.slowActionOverrideLabel", "Budget for {{eventType}}", {
              eventType,
            })}
            value={seconds}
            min={MIN_SECONDS}
            max={MAX_SECONDS}
            disabled={disabled}
            onChange={(event) => setSeconds(eventType, Number(event.target.value))}
          />
          <span className="w-14 text-[11px] text-muted-foreground">
            {t("admin.opsSettings.seconds", "seconds")}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => remove(eventType)}
            aria-label={t("admin.opsSettings.slowActionOverrideRemove", "Remove {{eventType}}", {
              eventType,
            })}
          >
            {t("admin.opsSettings.remove", "Remove")}
          </Button>
        </div>
      ))}

      <div className="flex items-center gap-2">
        <Input
          className="flex-1 font-mono text-[11px]"
          placeholder={t("admin.opsSettings.slowActionOverridePlaceholder", "OPPORTUNITY.GENERATE")}
          aria-label={t("admin.opsSettings.slowActionOverrideNew", "Event type to override")}
          value={newEventType}
          disabled={disabled || isFull}
          onChange={(event) => setNewEventType(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <Button variant="outline" size="sm" disabled={!canAdd} onClick={add}>
          {t("admin.opsSettings.slowActionOverrideAdd", "Add override")}
        </Button>
      </div>
    </div>
  );
}

/**
 * A number input hands back anything the user typed, including an empty string
 * as `NaN`. Clamping here keeps the draft inside the bounds the API enforces,
 * so a save is never rejected for a value the form allowed.
 */
function clamp(seconds: number): number {
  if (!Number.isFinite(seconds)) return MIN_SECONDS;
  return Math.min(MAX_SECONDS, Math.max(MIN_SECONDS, Math.round(seconds)));
}
